import type { SubEntryType } from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Label,
  ModalBody,
} from "@tandemdiabetes/sugarmate-react-components"
import { useEffect } from "react"

import { useEntryModalPrivateContext } from "../EntryModalPrivateContext"
import { SubEntryFooter } from "../SubEntryFooter"
import { SubEntryHeader } from "../SubEntryHeader"
import { useUpdateActivity } from "../useUpdateActivity"
import { AccountMedicationButton } from "./AccountMedicationButton"
import { accountMedicationToActivityMedication } from "./accountMedicationToActivityMedication"
import { MedicationFormNameSelect } from "./MedicationFormNameSelect"
import { MedicationFormUnitsRangeInput } from "./MedicationFormUnitsRangeInput"
import { useOnAccountMedicationCreated } from "./useOnAccountMedicationCreated"
import { useOnCurrentAccountMedicationDeleted } from "./useOnCurrentAccountMedicationDeleted"
import { useOnCurrentAccountMedicationUpdated } from "./useOnCurrentAccountMedicationUpdated"
import { AccountMedicationModal } from "src/components/AccountMedicationModal"
import { AccountMedicationLoadingProvider } from "src/contexts/LoadingContexts"
import {
  useAccountMedication,
  useAccountMedications,
} from "src/hooks"
import styles from "src/styles/entryModal.module.scss"
import type {
  EditableSubEntry,
  EditableUserEntry,
} from "src/types"

interface Props {
  currentSubEntry: EditableSubEntry<ActivityType.Medication>;
  updateSubEntry: (s: Partial<EditableSubEntry<ActivityType.Medication>>) => void;
  currentUserEntry: EditableUserEntry;
  setCurrentUserEntry: (userEntry: EditableUserEntry) => void;
}

/*
 * renders the form for editing a medication subentry
 */
export function MedicationForm(props: Props) {
  const {
    currentSubEntry,
    updateSubEntry,
    currentUserEntry,
    setCurrentUserEntry,
  } = props

  const { med: activity } = currentSubEntry

  const updateActivity = useUpdateActivity(
    ActivityType.Medication,
    activity,
    updateSubEntry,
  )

  const { readOnly } = useEntryModalPrivateContext()

  const { result: accountMedications } = useAccountMedications()
  const onlyAccountMedication = accountMedications.length === 1 ? accountMedications[0] : undefined

  // initialize the activity to the only account medication if the user only has one
  useEffect(
    () => {
      if (onlyAccountMedication && !currentSubEntry.med?.medication) {
        updateActivity({
          medication: accountMedicationToActivityMedication(onlyAccountMedication),
        })
      }
    },
    [
      currentSubEntry.med,
      updateActivity,
      onlyAccountMedication,
    ],
  )

  // the account medication currently referenced in the medication activity
  const currentAccountMedication = useAccountMedication(
    activity?.medication?.account_med_id ?? "",
  )

  // if the current account medication is updated, upate the activity too
  useOnCurrentAccountMedicationUpdated(
    activity,
    updateActivity,
    currentAccountMedication,
  )

  // if a new account medication is created then use it for the activity
  useOnAccountMedicationCreated(
    activity,
    updateActivity,
  )

  // reset the activity if it is a new activity and its account medication is removed
  useOnCurrentAccountMedicationDeleted(
    activity,
    updateActivity,
    currentAccountMedication,
    currentSubEntry,
  )

  return (
    <>
      <SubEntryHeader
        activityType={ActivityType.Medication}
        currentSubEntry={currentSubEntry}
        currentUserEntry={currentUserEntry}
        setCurrentUserEntry={setCurrentUserEntry}
        readOnly={readOnly}
        closeBtnId="med-entry-close-btn"
      />
      <ModalBody className={styles.modalBody}>
        <MedicationFormUnitsRangeInput
          activity={activity}
          updateActivity={updateActivity}
          units={activity?.medication?.units ?? currentAccountMedication?.units}
          readOnly={readOnly}
        />
        {readOnly ? (
          activity?.medication?.name ? (
            <Label>
              {activity.medication.name}
            </Label>
          ) : null
        ) : (
          <div className={styles.row}>
            <AccountMedicationLoadingProvider>
              <MedicationFormNameSelect
                activity={activity}
                updateActivity={updateActivity}
              />
            </AccountMedicationLoadingProvider>
            <AccountMedicationModal>
              <AccountMedicationButton
                currentAccountMedication={currentAccountMedication}
              />
            </AccountMedicationModal>
          </div>
        )}
        <SubEntryFooter
          currentSubEntry={currentSubEntry}
          currentUserEntry={currentUserEntry}
          updateSubEntry={updateSubEntry as (s: Partial<EditableSubEntry<SubEntryType>>) => void}
          readOnly={readOnly}
        />
      </ModalBody>
    </>
  )
}
